import { useState, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  BarChart3, Download, CheckCircle2, XCircle, QrCode,
  Smartphone, Tablet, Monitor, HelpCircle, MapPin, Loader2, FileText, RefreshCw,
} from "lucide-react";
import { jsPDF } from "jspdf";
import { registerPdfFont, PDF_FONT } from "./pdf-font";
import { cn } from "./cn";
import type { QrAnalytics, QrAnalyticsProps } from "./types";

// ─── Card shell (matches dashboard) ───────────────────────────
const card = "bg-card dark:bg-[rgba(27,24,23,0.75)] dark:backdrop-blur-xl border border-border dark:border-primary/20 shadow-sm rounded-2xl overflow-hidden";
const sectionLabel = "text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground";

// ─── Toast (matches settings.tsx) ─────────────────────────────
function Toast({ msg, ok, onDone }: { msg: string; ok: boolean; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2800);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div
      className={cn(
        "fixed bottom-6 left-1/2 -translate-x-1/2 z-[200]",
        "flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium",
        "animate-in slide-in-from-bottom-4 fade-in duration-300",
        ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white",
      )}
      role="status"
    >
      {ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
      {msg}
    </div>
  );
}

// ─── Time-series bar chart (hand-rolled SVG) ──────────────────
// Single-series magnitude over time → one hue (brand primary), baseline-anchored
// bars with the peak day emphasised. Decorative SVG; the wrapper carries role/label.
function TimeSeries({ data, peakLabel, daysLabel, formatDay }: {
  data: QrAnalytics["byDay"];
  peakLabel: string;
  daysLabel: string;
  formatDay: (day: string) => string;
}) {
  const n = data.length;
  const max = Math.max(...data.map((d) => d.scans), 1);
  const peakIdx = data.reduce((best, d, i) => (d.scans > data[best].scans ? i : best), 0);
  const peak = data[peakIdx];

  const H = 100;
  const slot = 100 / n;
  const barW = Math.min(slot * 0.62, 7);
  const scroll = n > 24;

  const first = data[0];
  const last = data[n - 1];

  return (
    <div
      role="img"
      aria-label={`${peakLabel}: ${formatDay(peak.day)} — ${peak.scans}. ${n} ${daysLabel}.`}
      className="flex flex-col gap-2"
    >
      {/* Peak callout */}
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold tabular-nums leading-none">{peak.scans}</span>
        <span className="text-xs text-muted-foreground">
          {peakLabel} · {formatDay(peak.day)}
        </span>
      </div>

      <div className={cn("w-full", scroll && "overflow-x-auto")}>
        <svg
          viewBox={`0 0 100 ${H}`}
          preserveAspectRatio="none"
          className="h-36 block"
          style={scroll ? { width: n * 26 } : { width: "100%" }}
          aria-hidden="true"
        >
          {data.map((d, i) => {
            const h = (d.scans / max) * (H - 4);
            const x = i * slot + (slot - barW) / 2;
            return (
              <rect
                key={d.day}
                x={x}
                y={H - h}
                width={barW}
                height={h}
                rx={1.5}
                className={cn(
                  "transition-[height,y] duration-700",
                  i === peakIdx ? "fill-primary" : "fill-primary/40",
                )}
              />
            );
          })}
        </svg>
      </div>

      {/* X-axis endpoints (recessive) */}
      {n > 1 && (
        <div className="flex items-center justify-between text-[10px] text-muted-foreground/70 tabular-nums">
          <span>{formatDay(first.day)}</span>
          <span>{formatDay(last.day)}</span>
        </div>
      )}
    </div>
  );
}

// ─── Horizontal magnitude bar (device / location rows) ────────
function BarRow({ label, sub, value, pct, icon }: {
  label: string;
  sub?: string;
  value: number;
  pct: number;
  icon?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      {icon && <span className="text-muted-foreground shrink-0">{icon}</span>}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-sm truncate">
            {label}
            {sub && <span className="text-muted-foreground"> · {sub}</span>}
          </span>
          <span className="text-xs font-semibold tabular-nums shrink-0">
            {value} <span className="text-muted-foreground font-normal">· {pct}%</span>
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-700"
            style={{ width: `${Math.max(pct, 2)}%` }}
          />
        </div>
      </div>
    </div>
  );
}

const DEVICE_ICON: Record<string, React.ReactNode> = {
  mobile: <Smartphone className="w-4 h-4" />,
  tablet: <Tablet className="w-4 h-4" />,
  desktop: <Monitor className="w-4 h-4" />,
  unknown: <HelpCircle className="w-4 h-4" />,
};

// ─── Page ─────────────────────────────────────────────────────
/**
 * Shared QR-analytics page — single source of truth for both organizer and
 * admin. The host app injects the API client (its own auth + base path) and the
 * wedding from its own context; everything else (layout, charts, PDF/CSV
 * export, i18n keys under `analytics.*`) lives here.
 */
export function QrAnalyticsPage({ slug, wedding, api }: QrAnalyticsProps) {
  const { t, i18n } = useTranslation();
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Slug-scoped so switching weddings refetches. Each host app owns its own
  // QueryClient, so the key does not need an app prefix.
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["qr-analytics", slug],
    queryFn: () => api.getQr(),
  });

  // Yenileme, ilk yüklemenin AYNI iskelet kartlarını gösterir (moderasyondaki
  // davranış). `isFetching`'e bağlamıyoruz: o, arka plan tazelemelerinde de
  // true olup sayfayı habersiz boşaltabilir — burada iskelet YALNIZ kullanıcı
  // Yenile'ye bastığında çıkar, yani her zaman kasıtlı bir geri bildirimdir.
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);

  const showSkeleton = isLoading || isRefreshing;

  const analytics = data?.data;

  const formatDay = useCallback(
    (day: string) =>
      new Date(`${day}T00:00:00`).toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "short",
      }),
    [i18n.language],
  );

  const handleCsv = useCallback(async () => {
    setDownloading(true);
    try {
      const blob = await api.exportCsv(i18n.language);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qr-analytics-${slug}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setToast({ msg: t("analytics.csvOk"), ok: true });
    } catch {
      setToast({ msg: t("analytics.csvErr"), ok: false });
    } finally {
      setDownloading(false);
    }
  }, [api, slug, t, i18n.language]);

  const handleDownloadPdf = useCallback(() => {
    if (!analytics || analytics.totals.scans === 0) return;
    setGeneratingPdf(true);
    try {
      const { totals, byDay, byDevice, byLocation } = analytics;

      const deviceName = (device: string) =>
        device === "mobile" ? t("analytics.deviceMobile")
          : device === "tablet" ? t("analytics.deviceTablet")
            : device === "desktop" ? t("analytics.deviceDesktop")
              : t("analytics.unknown");

      const doc = new jsPDF({ unit: "pt", format: "a4" });
      // Embedded Turkish-capable font (built-in helvetica mangles ş/ğ/ı/İ/ç/ö/ü).
      // Only a "normal" weight is embedded — hierarchy comes from size + colour.
      registerPdfFont(doc);
      doc.setFont(PDF_FONT, "normal");

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const marginX = 48;
      const rightX = pageWidth - marginX;
      const BRAND: [number, number, number] = [141, 107, 94]; // #8d6b5e
      let y = 68;

      const rule = (yy: number, color: [number, number, number] = [222, 222, 222]) => {
        doc.setDrawColor(...color);
        doc.setLineWidth(color === BRAND ? 1.2 : 0.6);
        doc.line(marginX, yy, rightX, yy);
      };
      // Section header: brand-coloured label with an accent rule under it.
      const section = (label: string) => {
        y += 6;
        doc.setFontSize(12);
        doc.setTextColor(...BRAND);
        doc.text(label.toUpperCase(), marginX, y);
        y += 8;
        rule(y, BRAND);
        y += 20;
      };
      // Two-column row: left label (dark), right value (right-aligned), thin divider.
      const row = (label: string, value: string) => {
        doc.setFontSize(11);
        doc.setTextColor(60);
        doc.text(label, marginX, y);
        doc.setTextColor(20);
        doc.text(value, rightX, y, { align: "right" });
        y += 9;
        rule(y);
        y += 15;
      };

      // ── Title band ──
      doc.setFontSize(22);
      doc.setTextColor(20);
      doc.text(wedding?.title || t("analytics.reportTitle"), marginX, y);
      y += 20;
      doc.setFontSize(12);
      doc.setTextColor(130);
      doc.text(t("analytics.reportTitle"), marginX, y);
      y += 16;
      rule(y);
      y += 26;

      // ── Özet ──
      section(t("analytics.summary"));
      row(t("analytics.scans"), `${totals.scans}`);
      row(t("analytics.unique"), `${totals.unique}`);
      row(t("analytics.registrations"), `${totals.registrations}`);
      row(t("analytics.conversion"), `%${totals.conversionRate}`);
      if (byDay.length > 0) {
        const peak = byDay.reduce((best, d) => (d.scans > best.scans ? d : best), byDay[0]);
        row(t("analytics.peakDay"), `${formatDay(peak.day)} — ${peak.scans}`);
      }
      y += 8;

      // ── Cihaz ──
      if (byDevice.length > 0) {
        section(t("analytics.byDevice"));
        for (const d of [...byDevice].sort((a, b) => b.scans - a.scans)) {
          row(deviceName(d.device), `${d.scans}`);
        }
        y += 8;
      }

      // ── Konum ──
      if (byLocation.length > 0) {
        section(t("analytics.byLocation"));
        for (const l of [...byLocation].sort((a, b) => b.scans - a.scans).slice(0, 8)) {
          const place = l.country && l.city
            ? `${l.city}, ${l.country}`
            : (l.city || l.country || t("analytics.unknown"));
          row(place, `${l.scans}`);
        }
      }

      // ── Footer ──
      const generatedAt = new Date().toLocaleDateString(i18n.language, {
        day: "numeric", month: "long", year: "numeric",
      });
      doc.setFontSize(9);
      doc.setTextColor(150);
      doc.text(`${t("analytics.generatedAt")}: ${generatedAt}`, marginX, pageHeight - 40);
      doc.text("wedding.mertbal.com", rightX, pageHeight - 40, { align: "right" });

      doc.save("qr-analytics-report.pdf");
      setToast({ msg: t("analytics.pdfOk"), ok: true });
    } catch {
      setToast({ msg: t("analytics.pdfErr"), ok: false });
    } finally {
      setGeneratingPdf(false);
    }
  }, [analytics, wedding, t, formatDay, i18n.language]);

  // ── Header (shared across all states) ──
  const header = (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5 min-w-0">
        <BarChart3 className="w-5 h-5 text-primary shrink-0" aria-hidden="true" />
        <h1 className="font-serif text-xl md:text-2xl font-bold truncate">{t("analytics.title")}</h1>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {/* Yenile — moderasyondaki "Şimdi yenile" ile aynı davranış: tarama
            sayıları düğün gecesi akarken sayfayı terk etmeden tazelenir. */}
        <button
          onClick={() => { void handleRefresh(); }}
          disabled={showSkeleton}
          aria-label={t("analytics.refresh")}
          className="shrink-0 inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-2 text-sm font-medium hover:bg-primary/5 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className={cn("w-4 h-4", showSkeleton && "animate-spin")} aria-hidden="true" />
          <span className="hidden sm:inline">{t("analytics.refresh")}</span>
        </button>
        <button
          onClick={handleDownloadPdf}
          disabled={generatingPdf || !analytics || analytics.totals.scans === 0}
          className="shrink-0 inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-2 text-sm font-medium hover:bg-primary/5 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {generatingPdf
            ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            : <FileText className="w-4 h-4" aria-hidden="true" />}
          {t("analytics.exportPdf")}
        </button>
        <button
          onClick={handleCsv}
          disabled={downloading || !analytics || analytics.totals.scans === 0}
          className="shrink-0 inline-flex items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-2 text-sm font-medium hover:bg-primary/5 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {downloading
            ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            : <Download className="w-4 h-4" aria-hidden="true" />}
          {t("analytics.exportCsv")}
        </button>
      </div>
    </div>
  );

  // ── Loading (ilk yükleme VE elle yenileme) ──
  if (showSkeleton) {
    return (
      <div className="space-y-6" aria-busy="true">
        {header}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={cn(card, "h-24 animate-pulse bg-muted")} />
          ))}
        </div>
        <div className={cn(card, "h-64 animate-pulse bg-muted")} />
        <div className={cn(card, "h-48 animate-pulse bg-muted")} />
      </div>
    );
  }

  // ── Error ──
  if (isError || !analytics) {
    return (
      <div className="space-y-6">
        {header}
        <div className={cn(card, "p-8 flex flex-col items-center gap-2 text-center")}>
          <XCircle className="w-8 h-8 text-rose-400" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">{t("analytics.csvErr")}</p>
        </div>
      </div>
    );
  }

  const { totals, byDay, byDevice, byLocation } = analytics;

  // ── Empty ──
  if (totals.scans === 0) {
    return (
      <div className="space-y-6">
        {header}
        <div className={cn(card, "p-10 flex flex-col items-center gap-3 text-center")}>
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
            <QrCode className="w-6 h-6 text-primary" aria-hidden="true" />
          </div>
          <p className="text-sm text-muted-foreground max-w-xs">{t("analytics.empty")}</p>
        </div>
      </div>
    );
  }

  const summary = [
    { label: t("analytics.scans"), value: `${totals.scans}` },
    { label: t("analytics.unique"), value: `${totals.unique}` },
    { label: t("analytics.registrations"), value: `${totals.registrations}` },
    { label: t("analytics.conversion"), value: `${totals.conversionRate}%` },
  ];

  const deviceTotal = byDevice.reduce((s, d) => s + d.scans, 0) || 1;
  const deviceLabel = (device: string) =>
    device === "mobile" ? t("analytics.deviceMobile")
      : device === "tablet" ? t("analytics.deviceTablet")
        : device === "desktop" ? t("analytics.deviceDesktop")
          : t("analytics.unknown");

  const locMax = Math.max(...byLocation.map((l) => l.scans), 1);

  const funnel = [
    { label: t("analytics.scans"), value: totals.scans },
    { label: t("analytics.unique"), value: totals.unique },
    { label: t("analytics.registrations"), value: totals.registrations },
  ];

  return (
    <div className="space-y-6">
      {header}

      {/* ── Summary strip ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {summary.map((s) => (
          <div key={s.label} className={cn(card, "p-4 flex flex-col gap-1.5")}>
            <p className={sectionLabel}>{s.label}</p>
            <p className="text-3xl font-bold tabular-nums leading-none">{s.value}</p>
          </div>
        ))}
      </div>

      {/* ── Time series ── */}
      <div className={card}>
        <div className="p-5 flex flex-col gap-3">
          <p className={sectionLabel}>{t("analytics.byDay")}</p>
          {byDay.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">{t("analytics.empty")}</p>
          ) : (
            <TimeSeries
              data={byDay}
              peakLabel={t("analytics.peak")}
              daysLabel={t("analytics.byDay").toLowerCase()}
              formatDay={formatDay}
            />
          )}
        </div>
      </div>

      {/* ── Device + Location ── */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Device */}
        <div className={card}>
          <div className="p-5 flex flex-col gap-4">
            <p className={sectionLabel}>{t("analytics.byDevice")}</p>
            {byDevice.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">{t("analytics.empty")}</p>
            ) : (
              <div
                role="img"
                aria-label={byDevice.map((d) => `${deviceLabel(d.device)} ${d.scans}`).join(", ")}
                className="flex flex-col gap-3.5"
              >
                {byDevice.map((d) => (
                  <BarRow
                    key={d.device}
                    label={deviceLabel(d.device)}
                    value={d.scans}
                    pct={Math.round((d.scans / deviceTotal) * 100)}
                    icon={DEVICE_ICON[d.device] ?? DEVICE_ICON.unknown}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Location */}
        <div className={card}>
          <div className="p-5 flex flex-col gap-4">
            <p className={sectionLabel}>{t("analytics.byLocation")}</p>
            {byLocation.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">{t("analytics.empty")}</p>
            ) : (
              <div className="flex flex-col gap-3.5">
                {byLocation.map((l, i) => {
                  const hasLoc = l.city || l.country;
                  const label = l.city && l.country
                    ? l.city
                    : (l.city || l.country || t("analytics.unknown"));
                  const sub = l.city && l.country ? l.country! : undefined;
                  return (
                    <BarRow
                      key={`${l.country ?? "?"}-${l.city ?? "?"}-${i}`}
                      label={hasLoc ? label : t("analytics.unknown")}
                      sub={sub}
                      value={l.scans}
                      pct={Math.round((l.scans / locMax) * 100)}
                      icon={<MapPin className={cn("w-4 h-4", !hasLoc && "opacity-50")} />}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Funnel ── */}
      <div className={card}>
        <div className="p-5 flex flex-col gap-4">
          <p className={sectionLabel}>{t("analytics.funnel")}</p>
          <div
            role="img"
            aria-label={funnel.map((f) => `${f.label} ${f.value}`).join(" → ")}
            className="flex flex-col gap-3"
          >
            {funnel.map((step, i) => {
              const pct = totals.scans === 0 ? 0 : Math.round((step.value / totals.scans) * 100);
              return (
                <div key={step.label} className="flex items-center gap-3">
                  <span className="w-24 shrink-0 text-xs text-muted-foreground truncate">{step.label}</span>
                  <div className="flex-1 h-6 rounded-lg bg-muted overflow-hidden">
                    <div
                      className={cn(
                        "h-full rounded-lg bg-primary/80 transition-all duration-700 flex items-center",
                        i === 0 ? "bg-primary" : i === 1 ? "bg-primary/70" : "bg-primary/50",
                      )}
                      style={{ width: `${Math.max(pct, 4)}%` }}
                      aria-hidden="true"
                    />
                  </div>
                  <span className="w-16 shrink-0 text-right text-sm font-semibold tabular-nums">
                    {step.value} <span className="text-muted-foreground font-normal text-xs">{pct}%</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {toast && <Toast msg={toast.msg} ok={toast.ok} onDone={() => setToast(null)} />}
    </div>
  );
}
